import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import {
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Gauge,
  Layers3,
  RefreshCcw,
  Sparkles,
  Target,
} from "lucide-react";
import {
  createExam,
  createFlashcardSession,
  createPracticeSession,
  createStudyPlan,
  getMyStudyPlan,
  regenerateStudyPlan,
  startStudyPlanTask,
  updateStudyPlan,
} from "utils/supabase";
import { linkStudyPlanTask } from "utils/studyPlanLink";
import LogoLoader from "components/Custom/LogoLoader.js";
import DateField from "components/Custom/DateField.js";

const PRESET_MINUTES = [30, 60, 120, 180, 240];
const DAY_OPTIONS = [
  { id: 1, label: "L" },
  { id: 2, label: "M" },
  { id: 3, label: "M" },
  { id: 4, label: "J" },
  { id: 5, label: "V" },
  { id: 6, label: "S" },
  { id: 7, label: "D" },
];
const TABS = ["Hoy", "Semana", "Hasta el ENARM"];
const DAYS_PER_LOAD = 3;

function formatDayLabel(dateLike) {
  if (!dateLike) return "";
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("es-MX", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}

function formatShortDate(dateLike) {
  if (!dateLike) return "";
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
  });
}

function formatInputDate(dateLike) {
  if (!dateLike) return "";
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return String(dateLike).slice(0, 10);
  return date.toISOString().slice(0, 10);
}

// La fecha de aplicacion del ENARM es el 30 de septiembre, asi que la usamos como sugerencia por default.
function getDefaultExamDate() {
  const now = new Date();
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  let target = new Date(now.getFullYear(), 8, 30);

  if (target <= tomorrow) {
    target = new Date(now.getFullYear() + 1, 8, 30);
  }

  const month = String(target.getMonth() + 1).padStart(2, "0");
  const day = String(target.getDate()).padStart(2, "0");
  return `${target.getFullYear()}-${month}-${day}`;
}

function formatMinutes(totalMinutes) {
  const minutes = Number(totalMinutes) || 0;
  if (minutes <= 0) return "0 min";

  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;

  if (hours === 0) return `${remaining} min`;
  if (remaining === 0) return `${hours} h`;
  return `${hours} h ${remaining} min`;
}

function getReadablePhase(phase) {
  const map = {
    coverage: "Cobertura",
    consolidation: "Consolidación",
    performance: "Rendimiento",
    final: "Recta final",
  };

  return map[phase] || phase || "General";
}

function getStudyPlanReasons(tasks = []) {
  const reasons = new Map();

  const addReason = (text, priority) => {
    if (text && !reasons.has(text)) {
      reasons.set(text, priority);
    }
  };

  tasks.forEach((task) => {
    const data = task?.reason_data || {};
    const specialty = task?.specialty_name || data?.specialty_name || null;
    const accuracy = Number(data?.accuracy);
    const incorrect = Number(data?.incorrect_14d);
    const dueFlashcards = Number(data?.due_flashcards);
    const daysSince = Number(data?.days_since_last_activity);
    const specificReasons = [];

    if (data?.accuracy != null && Number.isFinite(accuracy) && accuracy < 65) {
      specificReasons.push({
        priority: 1,
        text: specialty
          ? `Tu rendimiento en ${specialty} es de ${accuracy.toFixed(0)}%.`
          : "Tu rendimiento reciente está por debajo del objetivo.",
      });
    }

    if (data?.incorrect_14d != null && Number.isFinite(incorrect) && incorrect > 0) {
      specificReasons.push({
        priority: 2,
        text: specialty
          ? `Has tenido ${incorrect} respuestas incorrectas recientemente en ${specialty}.`
          : `Has tenido ${incorrect} respuestas incorrectas recientemente.`,
      });
    }

    if (data?.due_flashcards != null && Number.isFinite(dueFlashcards) && dueFlashcards > 0) {
      specificReasons.push({
        priority: 3,
        text: specialty
          ? `Tienes ${dueFlashcards} flashcards pendientes de ${specialty}.`
          : `Tienes ${dueFlashcards} flashcards pendientes de repaso.`,
      });
    }

    if (data?.days_since_last_activity != null && Number.isFinite(daysSince) && daysSince >= 5) {
      specificReasons.push({
        priority: 4,
        text: specialty
          ? `Han pasado ${daysSince} días desde que practicaste ${specialty}.`
          : `Han pasado ${daysSince} días desde tu última práctica.`,
      });
    }

    specificReasons.forEach((reason) => addReason(reason.text, reason.priority));

    if (specificReasons.length > 0) return;

    switch (task?.reason_code) {
      case "low_coverage":
        addReason(
          specialty
            ? `${specialty} necesita mayor cobertura dentro de tu preparación.`
            : "Esta área necesita mayor cobertura dentro de tu preparación.",
          5
        );
        break;
      case "simulator_schedule":
        addReason("Hoy corresponde realizar un simulador para medir tu progreso.", 6);
        break;
      case "balanced_rotation":
        addReason(
          specialty
            ? `Incluimos ${specialty} para mantener una preparación equilibrada.`
            : "Incluimos esta especialidad para mantener una preparación equilibrada.",
          7
        );
        break;
      default:
        break;
    }
  });

  return [...reasons.entries()]
    .sort(([, firstPriority], [, secondPriority]) => firstPriority - secondPriority)
    .map(([text]) => text);
}

function formatTaskType(type) {
  const labels = {
    practice: "Práctica",
    incorrect_review: "Repaso de errores",
    flashcards: "Flashcards",
    simulator: "Simulador ENARM",
    light_review: "Repaso ligero",
  };

  return labels[type] || type;
}

function getTaskActionLabel(task) {
  if (!task) return "Comenzar";
  if (task.status === "completed") return "Completado";
  if (task.status === "in_progress") return "Continuar";
  return "Comenzar";
}

function getTaskIcon(type) {
  switch (type) {
    case "practice":
      return ClipboardList;
    case "incorrect_review":
      return BookOpen;
    case "flashcards":
      return Layers3;
    case "simulator":
      return Target;
    case "light_review":
      return Sparkles;
    default:
      return Clock3;
  }
}

function shouldRegeneratePlan(plan) {
  if (!plan?.last_generated_at) return false;

  const generated = new Date(plan.last_generated_at);
  const now = new Date();

  if (Number.isNaN(generated.getTime())) return false;

  const generatedDay = new Date(
    generated.getFullYear(),
    generated.getMonth(),
    generated.getDate()
  );
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return generatedDay.getTime() < today.getTime();
}

function isExamDatePast(examDate) {
  if (!examDate) return false;

  const date = new Date(examDate);
  if (Number.isNaN(date.getTime())) return false;

  const examDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return examDay.getTime() < today.getTime();
}

function getNextTodayTask(tasks = []) {
  const ordered = [...tasks].sort(
    (a, b) => Number(a.task_order ?? 999) - Number(b.task_order ?? 999)
  );

  const inProgress = ordered.find((task) => task.status === "in_progress");
  if (inProgress) return inProgress;

  return ordered.find((task) => task.status === "pending") || ordered[0] || null;
}

function StudyPlanTaskCard({ task, onStart, primary = false, running = false }) {
  const Icon = getTaskIcon(task?.type);
  const actionLabel = getTaskActionLabel(task);
  const taskReasons = getStudyPlanReasons(task ? [task] : []);

  if (!task) return null;

  return (
    <div
      className={`flex flex-wrap items-start justify-between gap-3 rounded-2xl border p-4 sm:block sm:gap-0 ${
        primary ? "border-indigo-200 bg-indigo-50 shadow-sm" : "border-blueGray-200 bg-white"
      }`}
    >
      <style>
        {`
          .study-plan-reason-mobile { display: none; }
          @media (max-width: 639px) {
            .study-plan-reason-desktop { display: none; }
            .study-plan-reason-mobile { display: block; }
          }
        `}
      </style>
      <div className="flex w-full items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3 sm:flex-1">
          <div
              className={`mt-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl p-2 sm:h-12 sm:w-12 ${
              primary ? "bg-indigo-600 text-white" : "bg-blueGray-100 text-blueGray-700"
            }`}
          >
            <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-widest text-blueGray-500">
              {formatTaskType(task.type)}
            </p>
            <p className="mt-1 text-base font-semibold text-blueGray-800">
              {task.specialty_name || "ENARM"}
            </p>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-blueGray-600">
              <span>{task.target_count ?? 0} preguntas</span>
              <span>•</span>
              <span>{task.estimated_minutes ?? 0} min</span>
            </div>
            {taskReasons.length > 0 && (
              <p className="study-plan-reason-desktop mt-2 break-words text-sm leading-6 text-blueGray-600">
                {taskReasons.join(" ")}
              </p>
            )}
          </div>
        </div>

        {task.status === "completed" ? (
          <div className="flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">
            <CheckCircle2 size={14} />
            Completado
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onStart(task)}
            disabled={running}
            className={`flex-shrink-0 whitespace-nowrap rounded-xl px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-70 ${
              task.status === "in_progress"
                ? "bg-indigo-600 hover:bg-indigo-700"
                : "bg-violet-600 hover:bg-violet-700"
            }`}
          >
            {running ? "Cargando..." : actionLabel}
          </button>
        )}
      </div>

      {taskReasons.length > 0 && (
        <p className="study-plan-reason-mobile w-full text-sm leading-6 text-blueGray-600">
          {taskReasons.join(" ")}
        </p>
      )}
    </div>
  );
}

function StudyPlanDayCard({ day }) {
  const tasks = day?.tasks || [];

  return (
    <div className="rounded-2xl border border-blueGray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-blueGray-500">
            {formatDayLabel(day?.date)}
          </p>
          <p className="mt-1 text-sm font-semibold text-blueGray-800">
            {day?.is_study_day ? getReadablePhase(day?.phase) : "Día de descanso"}
          </p>
        </div>

        <div className="text-right text-xs text-blueGray-500">
          <div>{day?.estimated_minutes ?? 0} min</div>
          <div className="mt-1 font-medium text-blueGray-700">
            {Math.round(day?.completion_percentage ?? 0)}%
          </div>
        </div>
      </div>

      <div className="mt-3 h-2 overflow-hidden rounded-full bg-blueGray-200">
        <div
          className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-indigo-400"
          style={{
            width: `${Math.min(100, Math.max(0, Number(day?.completion_percentage || 0)))}%`,
          }}
        />
      </div>

      {day?.is_study_day ? (
        <div className="mt-4 space-y-2">
          {tasks.length > 0 ? (
            tasks.slice(0, 2).map((task) => (
              <div key={task.id} className="text-sm text-blueGray-600">
                • {task.specialty_name || formatTaskType(task.type)} · {task.target_count ?? 0}
              </div>
            ))
          ) : (
            <div className="text-sm text-blueGray-500">Sin tareas asignadas.</div>
          )}
        </div>
      ) : (
        <div className="mt-4 text-sm text-blueGray-500">Hoy es un día de descanso.</div>
      )}
    </div>
  );
}

function StudyPlanOnboarding({ draft, setDraft, setCustomMinutes, onCreate, isCreating }) {
  const selectedDays = new Set(draft.studyDays || []);

  const toggleDay = (dayId) => {
    const exists = selectedDays.has(dayId);

    setDraft((prev) => ({
      ...prev,
      studyDays: exists
        ? prev.studyDays.filter((id) => id !== dayId)
        : [...prev.studyDays, dayId].sort((a, b) => a - b),
    }));
  };

  return (
    <div className="rounded-3xl border border-blueGray-200 bg-white p-6 shadow-sm">
      <h2 className="text-2xl font-bold text-blueGray-900">Crea tu Plan ENARM</h2>
      <p className="mt-2 text-sm text-blueGray-600">
        ENARM Apex organizará qué estudiar cada día según tu rendimiento y el tiempo que falta para tu examen.
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-blueGray-700">Fecha del ENARM</label>
          <DateField
            value={draft.examDate}
            min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, examDate: event.target.value }))
            }
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-blueGray-700">Tiempo de estudio diario</label>
          <div className="flex flex-wrap gap-2">
            {PRESET_MINUTES.map((minutes) => (
              <button
                key={minutes}
                type="button"
                onClick={() => {
                  setDraft((prev) => ({ ...prev, dailyMinutes: minutes }));
                  setCustomMinutes("");
                }}
                className={`rounded-xl border px-3 py-2 text-sm font-medium ${
                  draft.dailyMinutes === minutes
                    ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                    : "border-blueGray-200 bg-white text-blueGray-700"
                }`}
              >
                {minutes >= 60 ? `${minutes / 60} h` : `${minutes} min`}
              </button>
            ))}

            <button
              type="button"
              onClick={() => setDraft((prev) => ({ ...prev, dailyMinutes: "" }))}
              className={`rounded-xl border px-3 py-2 text-sm font-medium ${
                draft.dailyMinutes === ""
                  ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                  : "border-blueGray-200 bg-white text-blueGray-700"
              }`}
            >
              Personalizado
            </button>
          </div>

          {draft.dailyMinutes === "" && (
            <input
              type="number"
              min={15}
              step={15}
              placeholder="Minutos diarios"
              onChange={(event) => setCustomMinutes(event.target.value)}
              className="mt-2 w-full rounded-xl border border-blueGray-200 bg-blueGray-50 px-3 py-2.5 text-blueGray-800 outline-none focus:border-indigo-400"
            />
          )}
        </div>
      </div>

      <div className="mt-6">
        <label className="block text-sm font-medium text-blueGray-700">Días disponibles</label>
        <div className="mt-3 flex flex-wrap gap-2">
          {DAY_OPTIONS.map((day) => {
            const active = selectedDays.has(day.id);
            return (
              <button
                key={day.id}
                type="button"
                onClick={() => toggleDay(day.id)}
                className={`flex h-10 w-10 items-center justify-center rounded-xl border text-sm font-medium ${
                  active
                    ? "border-indigo-500 bg-indigo-600 text-white"
                    : "border-blueGray-200 bg-blueGray-50 text-blueGray-700"
                }`}
              >
                {day.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        <button
          type="button"
          onClick={onCreate}
          disabled={isCreating}
          className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isCreating ? "Creando..." : "Crear mi plan"}
        </button>
      </div>
    </div>
  );
}

export default function PlanEstudio() {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState("Hoy");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [planData, setPlanData] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingPlan, setIsSavingPlan] = useState(false);
  const [startingTaskId, setStartingTaskId] = useState(null);
  const [visibleDaysCount, setVisibleDaysCount] = useState(DAYS_PER_LOAD);
  const [showSplash, setShowSplash] = useState(true);
  const [draft, setDraft] = useState({
    examDate: getDefaultExamDate(),
    dailyMinutes: 60,
    studyDays: [1, 2, 3, 4, 5],
  });
  const [customMinutes, setCustomMinutes] = useState("");
  const hasAutoOpenedExpiredModal = useRef(false);

  const loadPlan = async ({ silent = false } = {}) => {
    if (silent) {
      setIsRefreshing(true);
      setError("");
    } else {
      setLoading(true);
    }

    try {
      const { data, error: planError } = await getMyStudyPlan();

      if (planError) {
        throw planError;
      }

      setPlanData(data || null);

      if (data?.has_plan && data?.plan && shouldRegeneratePlan(data.plan)) {
        const { error: regenerateError } = await regenerateStudyPlan(data.plan.id);

        if (regenerateError) {
          console.error("Error regenerando el plan:", regenerateError);
        } else {
          const { data: refreshedData, error: refreshedError } = await getMyStudyPlan();
          if (!refreshedError && refreshedData) {
            setPlanData(refreshedData);
          }
        }
      }
    } catch (err) {
      console.error("Error cargando plan:", err);
      setError("No pudimos cargar tu plan de estudio. Intenta nuevamente.");
    } finally {
      setLoading(false);
      if (silent) setIsRefreshing(false);
    }
  };

  const handleRefreshPlan = () => {
    if (!isRefreshing) {
      loadPlan({ silent: true });
    }
  };

  const handleOpenEditModal = () => {
    const planStudyDays = Array.isArray(planData?.plan?.study_days)
      ? planData.plan.study_days
      : draft.studyDays;

    setDraft({
      examDate: formatInputDate(planData?.plan?.exam_date),
      dailyMinutes: Number(planData?.plan?.daily_minutes) || 60,
      studyDays: planStudyDays,
    });
    setCustomMinutes("");
    setError("");
    setIsEditModalOpen(true);
  };

  const handleSavePlan = async () => {
    const selectedDate = draft.examDate;
    const dailyMinutes = Number(draft.dailyMinutes);

    if (!selectedDate) {
      setError("Selecciona una fecha del ENARM.");
      return;
    }

    if (!Number.isFinite(dailyMinutes) || dailyMinutes <= 0) {
      setError("Selecciona un tiempo de estudio válido.");
      return;
    }

    if (!draft.studyDays || draft.studyDays.length === 0) {
      setError("Selecciona al menos un día de estudio.");
      return;
    }

    try {
      setIsSavingPlan(true);
      setError("");

      const previousExamDate = formatInputDate(planData?.plan?.exam_date);
      const examDateChanged = Boolean(previousExamDate) && previousExamDate !== selectedDate;

      const { error: updateError } = await updateStudyPlan(
        planData?.plan?.id,
        selectedDate,
        dailyMinutes,
        draft.studyDays
      );

      if (updateError) throw updateError;

      // Si cambia la fecha del ENARM el calendario ya no es valido, hay que regenerar el plan.
      if (examDateChanged) {
        const { error: regenerateError } = await regenerateStudyPlan(planData.plan.id);
        if (regenerateError) {
          console.error("Error regenerando el plan tras cambiar la fecha:", regenerateError);
        }
      }

      setIsEditModalOpen(false);
      await loadPlan({ silent: true });
    } catch (err) {
      console.error("Error actualizando plan:", err);
      setError(err?.message || "No pudimos actualizar tu plan. Intenta nuevamente.");
    } finally {
      setIsSavingPlan(false);
    }
  };

  const toggleEditStudyDay = (dayId) => {
    setDraft((previous) => ({
      ...previous,
      studyDays: previous.studyDays.includes(dayId)
        ? previous.studyDays.filter((id) => id !== dayId)
        : [...previous.studyDays, dayId].sort((first, second) => first - second),
    }));
  };

  useEffect(() => {
    loadPlan();
  }, []);

  useEffect(() => {
    if (loading) return undefined;

    // let the logo mouth-open animation finish before revealing the app
    const timeoutId = setTimeout(() => setShowSplash(false), 950);
    return () => clearTimeout(timeoutId);
  }, [loading]);

  useEffect(() => {
    if (loading || hasAutoOpenedExpiredModal.current) return;
    if (!planData?.has_plan || !isExamDatePast(planData?.plan?.exam_date)) return;

    hasAutoOpenedExpiredModal.current = true;
    handleOpenEditModal();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, planData]);

  const plan = useMemo(() => planData?.plan || null, [planData]);
  const today = useMemo(() => planData?.today || null, [planData]);
  const allDays = useMemo(() => planData?.days || [], [planData]);
  const visiblePlanDays = useMemo(
    () => allDays.slice(0, visibleDaysCount),
    [allDays, visibleDaysCount]
  );

  const nextTask = useMemo(() => {
    if (!today?.tasks?.length) return null;
    return getNextTodayTask(today.tasks);
  }, [today]);

  const todayStats = useMemo(() => {
    const tasks = today?.tasks || [];
    const completedCount = tasks.filter((task) => task.status === "completed").length;
    const pendingCount = tasks.filter((task) => task.status !== "completed").length;
    const remainingMinutes = tasks
      .filter((task) => task.status !== "completed")
      .reduce((total, task) => total + (Number(task.estimated_minutes) || 0), 0);

    return { completedCount, pendingCount, remainingMinutes };
  }, [today]);

  const todayReasons = useMemo(() => {
    const tasks = today?.tasks || [];
    return getStudyPlanReasons(tasks);
  }, [today]);

  const dailyGoalText = useMemo(() => {
    const pendingCount = Math.max(0, Number(todayStats.pendingCount) || 0);
    const remainingMinutes = Math.max(0, Number(todayStats.remainingMinutes) || 0);

    if (pendingCount === 0) {
      return "Hoy ya completaste tus actividades programadas. Buen trabajo.";
    }

    if (remainingMinutes > 0) {
      return `Completa ${pendingCount} actividad${pendingCount === 1 ? "" : "es"} y mantén tu ritmo de estudio.`;
    }

    return "Mantén tu ritmo de estudio y completa la siguiente actividad del plan.";
  }, [todayStats]);

  const handleCreatePlan = async () => {
    const selectedDate = draft.examDate;
    const normalizedMinutes =
      draft.dailyMinutes === "" ? Number(customMinutes) : Number(draft.dailyMinutes);

    if (!selectedDate) {
      setError("Selecciona una fecha del ENARM.");
      return;
    }

    const todayValue = new Date();
    todayValue.setHours(0, 0, 0, 0);
    const examDateValue = new Date(`${selectedDate}T00:00:00`);

    if (examDateValue <= todayValue) {
      setError("La fecha del ENARM debe ser posterior a hoy.");
      return;
    }

    if (!Number.isFinite(normalizedMinutes) || normalizedMinutes <= 0) {
      setError("Selecciona un tiempo de estudio válido.");
      return;
    }

    if (!draft.studyDays || draft.studyDays.length === 0) {
      setError("Selecciona al menos un día de estudio.");
      return;
    }

    try {
      setIsCreating(true);
      setError("");

      const { error: createError } = await createStudyPlan(
        selectedDate,
        normalizedMinutes,
        draft.studyDays
      );

      if (createError) {
        throw createError;
      }

      await loadPlan();
    } catch (err) {
      console.error("Error creando plan:", err);
      setError("No pudimos crear tu plan de estudio. Intenta nuevamente.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleStartTask = async (task) => {
    if (!task || task.status === "completed") return;

    try {
      setStartingTaskId(task.id);

      switch (task.type) {
        case "simulator": {
          const existingExamId = task.result_data?.exam_id;
          if (existingExamId) {
            history.push(`/admin/simulador/${existingExamId}`);
            return;
          }

          const specialtyIds = [task.specialty_id].filter(Boolean);
          const totalQuestions = Number(task.target_count || 40);

          const { exam } = await createExam({
            selectedSpecialtyIds: specialtyIds.length ? specialtyIds : [task.specialty_id].filter(Boolean),
            totalQuestions,
            casePercentage: 0.4,
            difficultyFilter:
              task.metadata?.difficulty === "all" || !task.metadata?.difficulty
                ? "false"
                : task.metadata.difficulty,
            durationMinutes: Math.max(30, Math.round(totalQuestions * 1.2)),
            modo: "plan_estudio",
            studyPlanTaskId: task.id,
          });

          const { error: startError } = await startStudyPlanTask(task.id, { exam_id: exam.id });
          if (startError) console.error("Error starting study plan task (simulator):", startError);
          linkStudyPlanTask("exam", exam.id, task.id);
          history.push(`/admin/simulador/${exam.id}`);
          break;
        }

        case "practice":
        case "incorrect_review": {
          const existingSessionId = task.result_data?.study_session_id;
          if (existingSessionId) {
            history.push(`/admin/practica/${existingSessionId}`);
            return;
          }

          const { data } = await createPracticeSession(
            [task.specialty_id].filter(Boolean),
            Number(task.target_count || 0),
            null,
            false,
            false,
            task.type === "incorrect_review",
            40
          );

          const createdSession = Array.isArray(data) ? data[0] : data;
          const sessionId =
            createdSession?.study_session_id ||
            createdSession?.session_id ||
            createdSession?.id;

          if (!sessionId) {
            throw new Error("No se pudo crear la sesión de práctica.");
          }

          const { error: startError } = await startStudyPlanTask(task.id, { study_session_id: sessionId });
          if (startError) console.error("Error starting study plan task (practice):", startError);
          linkStudyPlanTask("practice", sessionId, task.id);
          history.push(`/admin/practica/${sessionId}`);
          break;
        }

        case "flashcards": {
          const existingSessionId = task.result_data?.flashcard_session_id;
          if (existingSessionId) {
            history.push(`/admin/flashcards/${existingSessionId}`);
            return;
          }

          const preferredMode = task.metadata?.preferred_mode || "due";
          const { data } = await createFlashcardSession(
            [task.specialty_id].filter(Boolean),
            Number(task.target_count || 20),
            preferredMode
          );

          const sessionId = data?.flashcard_session_id || data?.[0]?.flashcard_session_id || data?.id;

          if (!sessionId) {
            throw new Error("No se pudo crear la sesión de flashcards.");
          }

          const { error: startError } = await startStudyPlanTask(task.id, { flashcard_session_id: sessionId });
          if (startError) console.error("Error starting study plan task (flashcards):", startError);
          linkStudyPlanTask("flashcards", sessionId, task.id);
          history.push(`/admin/flashcards/${sessionId}`);
          break;
        }

        case "light_review":
        default:
          history.push("/admin/practica");
          break;
      }
    } catch (err) {
      console.error("Error iniciando tarea del plan:", err);
      setError("No pudimos iniciar esta actividad. Intenta nuevamente.");
    } finally {
      setStartingTaskId(null);
    }
  };

  const progress = Math.round(today?.completion_percentage ?? 0);
  const isRestDay = today?.is_study_day === false;
  const prioritySpecialty = useMemo(() => {
    const candidates = [
      planData?.plan?.priority_specialty_name,
      planData?.plan?.top_specialty_name,
      planData?.plan?.specialty_name,
      today?.tasks?.find((task) => task.specialty_name)?.specialty_name,
      today?.tasks?.[0]?.specialty_name,
      "Sin prioridad",
    ];

    return candidates.find(Boolean) || "Sin prioridad";
  }, [planData, today]);

  if (showSplash) {
    return (
      <div className="space-y-6 pb-3">
        <div>
          <h1 className="text-3xl font-bold leading-tight text-blueGray-900">Mi Plan ENARM</h1>
          <p className="mt-2 text-sm font-normal text-blueGray-500">Tu plan de estudio personalizado hasta el ENARM</p>
        </div>
        <div className="flex min-h-[360px] w-full flex-col items-center justify-center rounded-3xl border border-blueGray-200 bg-white p-8">
          <div className="flex min-h-[280px] w-full flex-col items-center justify-center">
            <LogoLoader size={110} label="Cargando tu plan..." fullScreen={false} done={!loading} />
          </div>
        </div>
      </div>
    );
  }

  if (!planData?.has_plan) {
    return (
      <div className="space-y-6 pb-3">
        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        <StudyPlanOnboarding
          draft={draft}
          setDraft={setDraft}
          setCustomMinutes={setCustomMinutes}
          onCreate={handleCreatePlan}
          isCreating={isCreating}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-3">
      <header className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold leading-tight text-blueGray-900">Mi Plan ENARM</h1>
          <p className="mt-2 text-sm font-normal text-blueGray-500">Tu plan de estudio personalizado hasta el ENARM</p>
        </div>
        <button
          type="button"
          onClick={handleOpenEditModal}
          className="inline-flex items-center justify-center rounded-xl border border-blueGray-200 bg-white px-4 py-2 text-sm font-semibold text-blueGray-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
        >
          Editar plan
        </button>
      </header>

      <section className="grid grid-cols-2 gap-3 xl:grid-cols-4 xl:gap-4">
        <div className="flex h-full min-h-[150px] flex-col justify-between rounded-2xl border border-blueGray-200 bg-white p-3 shadow-sm sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-700 sm:h-11 sm:w-11">
              <CalendarDays size={18} className="sm:h-5 sm:w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-blueGray-500 sm:text-sm">Días para el ENARM</p>
              <p className="mt-1 text-2xl font-bold text-blueGray-900">{plan?.days_until_exam ?? 0}</p>
            </div>
          </div>
        </div>

        <div className="flex h-full min-h-[150px] flex-col justify-between rounded-2xl border border-blueGray-200 bg-white p-3 shadow-sm sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-lightBlue-100 text-lightBlue-700 sm:h-11 sm:w-11">
              <Clock3 size={18} className="sm:h-5 sm:w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-blueGray-500 sm:text-sm">Tiempo diario</p>
              <p className="mt-1 text-2xl font-bold text-blueGray-900">{formatMinutes(plan?.daily_minutes)}</p>
            </div>
          </div>
        </div>

        <div className="flex h-full min-h-[150px] flex-col justify-between rounded-2xl border border-blueGray-200 bg-white p-3 shadow-sm sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 sm:h-11 sm:w-11">
              <Gauge size={18} className="sm:h-5 sm:w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-blueGray-500 sm:text-sm">Progreso</p>
              <p className="mt-1 text-2xl font-bold text-blueGray-900">{Math.round(progress)}%</p>
            </div>
          </div>
        </div>

        <div className="flex h-full min-h-[150px] flex-col justify-between rounded-2xl border border-blueGray-200 bg-white p-3 shadow-sm sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700 sm:h-11 sm:w-11">
              <Sparkles size={18} className="sm:h-5 sm:w-5" />
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <p className="text-xs text-blueGray-500 sm:text-sm">Prioridad</p>
              <p className="mt-1 truncate text-lg font-bold text-blueGray-900 sm:text-2xl">{prioritySpecialty}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-blueGray-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
              <Target size={22} />
            </div>
            <div>
              <p className="text-sm font-medium text-blueGray-500">{isRestDay ? "Día de descanso" : "Hoy"}</p>
              <h2 className="text-2xl font-semibold text-blueGray-900">
                {isRestDay ? "Hoy no tienes actividades" : "Tu objetivo de hoy"}
              </h2>
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-3 lg:max-w-xl lg:items-end">
            {isRestDay ? (
              <p className="text-sm text-blueGray-500">
                Tómate el día para descansar y volver con energía a tu preparación.
              </p>
            ) : (
              <>
                <div className="flex w-full items-center gap-3 lg:max-w-md">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-blueGray-200">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-violet-500 to-indigo-500"
                      style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                    />
                  </div>
                  <span className="text-right text-sm font-semibold text-blueGray-700" style={{ minWidth: 48 }}>{progress}%</span>
                </div>
                <p className="text-sm text-blueGray-500">{dailyGoalText}</p>
              </>
            )}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row lg:w-auto">
            {isRestDay ? (
              <div className="flex items-center justify-center gap-2 rounded-xl bg-blueGray-100 px-4 py-3 text-sm font-semibold text-blueGray-600">
                Día de descanso
              </div>
            ) : todayStats.pendingCount === 0 ? (
              <div className="flex items-center justify-center gap-2 rounded-xl bg-emerald-100 px-4 py-3 text-sm font-semibold text-emerald-700">
                <CheckCircle2 size={16} />
                Completado
              </div>
            ) : (
              <button
                type="button"
                onClick={() => nextTask && handleStartTask(nextTask)}
                className="rounded-xl bg-violet-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-violet-700"
              >
                {nextTask ? (nextTask.status === "in_progress" ? "Continuar estudiando" : "Comenzar") : "Sin tarea"}
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          <div className="rounded-2xl border border-blueGray-200 bg-white p-2 shadow-sm">
            <div className="flex flex-wrap gap-2">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab);
                    if (tab === "Hasta el ENARM") {
                      setVisibleDaysCount(DAYS_PER_LOAD);
                    }
                  }}
                  className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                    activeTab === tab
                      ? "bg-violet-600 text-white shadow-sm"
                      : "bg-blueGray-100 text-blueGray-600 hover:bg-blueGray-200"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {activeTab === "Hoy" && (
            <div className="space-y-4">
              {nextTask && (
                <div className="rounded-2xl border border-blueGray-200 bg-blueGray-50 p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h3 className="text-xl font-bold text-blueGray-900">Siguiente actividad</h3>
                  </div>

                  <StudyPlanTaskCard
                    task={nextTask}
                    onStart={handleStartTask}
                    primary
                    running={startingTaskId === nextTask.id}
                  />
                </div>
              )}

              <div className="rounded-2xl border border-blueGray-200 bg-white p-4 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-xl font-bold text-blueGray-900">Actividades de hoy</h3>
                  <button
                    type="button"
                    onClick={handleRefreshPlan}
                    disabled={isRefreshing}
                    className="inline-flex items-center gap-2 rounded-xl border border-blueGray-200 bg-blueGray-50 px-3 py-2 text-sm text-blueGray-700 hover:bg-blueGray-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <RefreshCcw size={14} className={isRefreshing ? "animate-spin" : ""} />
                    {isRefreshing ? "Actualizando..." : "Actualizar"}
                  </button>
                </div>

                <div className="space-y-3">
                  {(today?.tasks || []).map((task) => (
                    <StudyPlanTaskCard
                      key={task.id}
                      task={task}
                      onStart={handleStartTask}
                      primary={task.id === nextTask?.id}
                      running={startingTaskId === task.id}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "Semana" && (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {(allDays || []).slice(0, 7).map((day) => (
                <StudyPlanDayCard key={day.id} day={day} />
              ))}
            </div>
          )}

          {activeTab === "Hasta el ENARM" && (
            <div className="space-y-4">
              {visiblePlanDays.map((day) => (
                <div key={day.id} className="rounded-2xl border border-blueGray-200 bg-blueGray-50 p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-blueGray-500">
                        {formatShortDate(day.date)}
                      </p>
                      <p className="mt-1 font-semibold text-blueGray-800">
                        {day.is_study_day ? getReadablePhase(day.phase) : "Día de descanso"}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-sm text-blueGray-600">
                      <span>{day.estimated_minutes ?? 0} min</span>
                      <span>•</span>
                      <span>{Math.round(day.completion_percentage ?? 0)}% completado</span>
                    </div>
                  </div>

                  {day.is_study_day && (day.tasks || []).length > 0 ? (
                    <div className="mt-4 space-y-2">
                      {(day.tasks || []).map((task) => (
                        <div key={task.id} className="rounded-xl border border-blueGray-200 bg-white p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-blueGray-800">
                                {task.specialty_name || formatTaskType(task.type)}
                              </p>
                              <p className="text-xs text-blueGray-500">
                                {formatTaskType(task.type)} • {task.target_count ?? 0} • {task.estimated_minutes ?? 0} min
                              </p>
                            </div>
                            <span
                              className={`rounded-full px-2 py-1 text-xs ${
                                task.status === "completed"
                                  ? "bg-emerald-100 font-medium text-emerald-700"
                                  : task.status === "pending"
                                  ? "bg-amber-100 font-medium text-amber-700"
                                  : "bg-blueGray-100 text-blueGray-700"
                              }`}
                            >
                              {task.status === "completed"
                                ? "Completado"
                                : task.status === "pending"
                                  ? "Pendiente"
                                  : task.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-4 text-sm text-blueGray-500">Sin actividades programadas.</div>
                  )}
                </div>
              ))}
              {visibleDaysCount < allDays.length && (
                <button
                  type="button"
                  onClick={() => setVisibleDaysCount((count) => count + DAYS_PER_LOAD)}
                  className="w-full rounded-xl border border-blueGray-200 bg-white px-4 py-3 text-sm font-semibold text-indigo-600 transition hover:border-indigo-300 hover:bg-indigo-50"
                >
                  Ver más
                </button>
              )}
            </div>
          )}
        </div>

        <aside className="space-y-4 xl:col-span-1">
          <div className="rounded-2xl border border-blueGray-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-blueGray-900">Progreso de hoy</h3>
            </div>

            <div className="flex justify-center py-3">
              <div className="relative flex h-32 w-32 items-center justify-center rounded-full bg-white">
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: `conic-gradient(#8b5cf6 0 ${progress}%, #e2e8f0 ${progress}% 100%)`,
                    WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 10px), black calc(100% - 10px))",
                    mask: "radial-gradient(farthest-side, transparent calc(100% - 10px), black calc(100% - 10px))",
                  }}
                />
                <div className="relative z-10 text-center">
                  <div className="text-3xl font-bold text-blueGray-900">{progress}%</div>
                  <div className="text-xs uppercase tracking-widest text-blueGray-500">completo</div>
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-2 text-sm text-blueGray-600">
              <div className="flex items-center justify-between rounded-xl bg-blueGray-50 p-2.5">
                <span>{todayStats.completedCount} actividades completadas</span>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-blueGray-50 p-2.5">
                <span>{todayStats.pendingCount} pendientes</span>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-blueGray-50 p-2.5">
                <span>Tiempo estimado restante</span>
                <span className="font-semibold text-blueGray-800">{formatMinutes(todayStats.remainingMinutes)}</span>
              </div>
            </div>
          </div>

          {todayReasons.length > 0 && (
            <div className="rounded-2xl border border-blueGray-200 bg-white p-4 shadow-sm">
              <h3 className="text-xl font-bold text-blueGray-900">¿Por qué estudiar esto?</h3>
              <ul className="mt-4 space-y-3 text-sm text-blueGray-600">
                {todayReasons.map((reason, index) => (
                  <li key={`${reason}-${index}`} className="flex gap-2">
                    <span className="mt-1 inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full bg-violet-500" />
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-2xl border border-blueGray-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-blueGray-900">Próximos días</h3>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {(allDays || []).slice(0, 3).map((day) => (
                <div key={day.id} className="rounded-xl border border-blueGray-200 bg-blueGray-50 p-2 text-center">
                  <p className="text-xs text-blueGray-500">{formatDayLabel(day.date).split(" ")[0]}</p>
                  <p className="mt-1 text-xs font-semibold text-blueGray-800">{formatShortDate(day.date)}</p>
                  <div className="mt-2 rounded-full bg-violet-100 px-2 py-1 text-xs font-medium text-violet-700">
                    {day.is_study_day ? (day.tasks?.length || 0) + " actividades" : "Descanso"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </section>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {isEditModalOpen && createPortal(
        <div
          className="fixed left-0 top-0 z-50 flex h-screen w-screen items-center justify-center overflow-y-auto bg-blueGray-900 bg-opacity-50 p-4"
          style={{ zIndex: 1400 }}
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isSavingPlan) {
              setIsEditModalOpen(false);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-study-plan-title"
            className="relative my-4 w-full max-w-lg overflow-y-auto rounded-2xl border border-blueGray-200 bg-white p-5 shadow-xl sm:p-6"
            style={{ maxHeight: "calc(100vh - 2rem)" }}
          >
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h2 id="edit-study-plan-title" className="text-xl font-bold text-blueGray-900">
                  Editar Plan ENARM
                </h2>
                <p className="mt-1 text-sm text-blueGray-500">Actualiza tu ritmo de preparación.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                disabled={isSavingPlan}
                aria-label="Cerrar modal"
                className="rounded-lg px-2 py-1 text-xl leading-none text-blueGray-400 hover:bg-blueGray-100 hover:text-blueGray-700 disabled:opacity-50"
              >
                ×
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <label htmlFor="edit-plan-exam-date" className="mb-2 block text-sm font-semibold text-blueGray-700">
                  Fecha del ENARM
                </label>
                <DateField
                  id="edit-plan-exam-date"
                  value={draft.examDate}
                  min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                  onChange={(event) => setDraft((previous) => ({ ...previous, examDate: event.target.value }))}
                />
              </div>

              <div>
                <p className="mb-2 text-sm font-semibold text-blueGray-700">Tiempo diario</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                  {PRESET_MINUTES.map((minutes) => (
                    <button
                      key={minutes}
                      type="button"
                      onClick={() => setDraft((previous) => ({ ...previous, dailyMinutes: minutes }))}
                      className={`rounded-xl border px-2 py-2 text-sm font-medium transition ${
                        Number(draft.dailyMinutes) === minutes
                          ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                          : "border-blueGray-200 bg-white text-blueGray-700 hover:bg-blueGray-50"
                      }`}
                    >
                      {minutes >= 60 ? `${minutes / 60} h` : `${minutes} min`}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-semibold text-blueGray-700">Días de estudio</p>
                <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
                  {DAY_OPTIONS.map((day) => {
                    const selected = draft.studyDays.includes(day.id);
                    return (
                      <button
                        key={day.id}
                        type="button"
                        onClick={() => toggleEditStudyDay(day.id)}
                        aria-pressed={selected}
                        className={`flex h-10 items-center justify-center rounded-xl border text-sm font-semibold transition ${
                          selected
                            ? "border-indigo-500 bg-indigo-600 text-white"
                            : "border-blueGray-200 bg-blueGray-50 text-blueGray-600 hover:bg-blueGray-100"
                        }`}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-blueGray-500">Selecciona al menos un día.</p>
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                disabled={isSavingPlan}
                className="rounded-xl border border-blueGray-200 px-4 py-2.5 text-sm font-semibold text-blueGray-700 hover:bg-blueGray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSavePlan}
                disabled={isSavingPlan}
                className="rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSavingPlan ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
